import { getFootballSeasonYear } from '../../../utils/season';
// src/components/layout/JoinGroupFlow.jsx
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { FAN_TYPE_MAPPINGS } from '../../utils/unified-credit-config';

// Make logger optional so build doesn't crash if it's missing
let _logger = null;
try {
  _logger = require('../../utils/logger').logger;
} catch (_) {
  _logger = {
    component: console.log,
    completion: console.log,
    success: console.log,
    network: console.log,
    database: console.log,
    debug: console.log,
    warn: console.warn ? console.warn : console.log,
    error: console.error,
  };
}
const logger = _logger;

const JoinGroupFlow = ({
  gameData = {},
  setGameData,
  setCurrentStep,
  inviteCode: propInviteCode,
  autoFilled,
  authUserDirectJoin,
  setNavigationData,
}) => {
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [foundGroup, setFoundGroup] = useState(null);
  const [groupPreview, setGroupPreview] = useState(null);
  const [autoFillProcessed, setAutoFillProcessed] = useState(false);
  const [autoSearchInProgress, setAutoSearchInProgress] = useState(false);

  const cleanupTimerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    // Hygiene: clear any stale payment flow flags on entry
    try {
      localStorage.removeItem('sbe-temp-game-data');
      localStorage.removeItem('sbe-temp-fan-type');
      localStorage.removeItem('sbe-temp-current-step');
      localStorage.removeItem('sbe-payment-timestamp');
      localStorage.removeItem('sbe-navigation-context');
      sessionStorage.removeItem('stripe-payment-active');
      sessionStorage.removeItem('stripe-payment-start');
      sessionStorage.removeItem('payment-security-token');
    } catch {}

    return () => {
      mountedRef.current = false;
      if (cleanupTimerRef.current) clearTimeout(cleanupTimerRef.current);
    };
  }, []);

  const normalizedCode = useMemo(
    () => String(inviteCode || '').trim().toUpperCase(),
    [inviteCode]
  );

  const hasFirebase =
    typeof window !== 'undefined' &&
    !!window.firebase &&
    !!window.db &&
    !!window.firebase.firestore;

  const FieldValue = hasFirebase ? window.firebase.firestore.FieldValue : null;

  // Helpers
  const mapFanTypeLabel = (ft) => {
    const backend = FAN_TYPE_MAPPINGS?.toBackend
      ? FAN_TYPE_MAPPINGS.toBackend(ft)
      : (ft === 'expert' ? 'diehard' : ft);
    if (backend === 'casual') return 'Casual Fan';
    if (backend === 'dedicated') return 'Dedicated Fan';
    if (backend === 'diehard') return 'DieHard Fan';
    return backend || '—';
  };

  const stripInviteFromUrl = useCallback((code) => {
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('invite');
      u.searchParams.delete('step');
      u.searchParams.delete('forced');
      const qs = u.searchParams.toString();
      window.history.replaceState({}, document.title, u.pathname + (qs ? `?${qs}` : ''));
      if (code) sessionStorage.setItem(`invite-accepted:${String(code).toUpperCase()}`, '1');
    } catch {}
  }, []);

  useEffect(() => {
    if (error && normalizedCode.length > 0) setError('');
  }, [normalizedCode, error]);

  const loadGroupByCode = async (code) => {
    if (!hasFirebase) throw new Error('Database not available');
    // Try docId == code first
    let snap = await window.db.collection('groups').doc(code).get();
    if (!snap.exists) {
      // Fallback: where inviteCode == code
      const q = await window.db.collection('groups').where('inviteCode', '==', code).limit(1).get();
      if (!q.empty) snap = q.docs[0];
    }
    if (!snap.exists) return null;

    const group = { id: snap.id, ...snap.data() };
    // Normalize fanType via shared mapping
    const backendFanType = FAN_TYPE_MAPPINGS?.toBackend
      ? FAN_TYPE_MAPPINGS.toBackend(group.fanType)
      : (group.fanType === 'expert' ? 'diehard' : group.fanType);

    return { ...group, fanType: backendFanType };
  };

  // Search
  const searchGroup = useCallback(async () => {
    const code = normalizedCode;
    logger.component('🔍 searchGroup', { code, autoFillProcessed, authUserDirectJoin, propInviteCode });

    if (!code) {
      if (!autoFillProcessed && !authUserDirectJoin && !propInviteCode) {
        setError('Please enter an invite code');
      } else {
        setError('');
      }
      return;
    }

    setLoading(true);
    setError('');
    setFoundGroup(null);

    try {
      const group = await loadGroupByCode(code);
      if (!group) {
        setError('Group not found. Please check your invite code.');
        return;
      }
      if (!group.team || !group.fanType) {
        setError('This group is not fully configured yet. Please wait for the creator to finish setup.');
        return;
      }
      if (group.status === 'completed' || group.status === 'archived') {
        setError('This group is no longer accepting new members.');
        return;
      }
      logger.success('✅ Found valid group', group);
      // Load a lightweight preview (members + recent submissions)
      try {
        const preview = { members: [], submitted: 0 };
        const membersObj = group.members || group.groupMembers || {};
        if (Array.isArray(membersObj)) {
          preview.members = membersObj.slice(0, 10).map(m => (typeof m === 'string' ? { id: m } : m));
        } else if (typeof membersObj === 'object') {
          preview.members = Object.keys(membersObj).slice(0, 10).map(uid => ({ id: uid, ...(membersObj[uid] || {}) }));
        }
        if (hasFirebase && window.db) {
          const submittedSnap = await window.db.collection('gameAnalysis')
            .where('groupId', '==', group.id)
            .where('status', '==', 'submitted')
            .limit(5)
            .get();
          preview.submitted = submittedSnap.size;
        }
        setGroupPreview(preview);
      } catch (e) {
        logger.warn('Group preview failed', e?.message || e);
        setGroupPreview(null);
      }
      if (mountedRef.current) setFoundGroup(group);
    } catch (err) {
      logger.error('❌ Error searching for group:', err);
      setError('Failed to search for group. Please try again.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [normalizedCode, autoFillProcessed, authUserDirectJoin, propInviteCode, hasFirebase]);

  // Join
  const joinGroup = useCallback(async () => {
    if (!foundGroup) {
      logger.component('❌ No foundGroup - cannot join');
      return;
    }
    if (!setGameData || !setCurrentStep) {
      logger.error('setGameData / setCurrentStep not provided');
      return;
    }

    const user = hasFirebase ? window.firebase.auth().currentUser : null;
    if (!user || user.isAnonymous) {
      logger.error('❌ User not authenticated for group join');
      setError('You must be signed in to join a group. Please sign in and try again.');
      return;
    }

    logger.component('🔐 User authenticated for join', { uid: user.uid, email: user.email });

    setLoading(true);
    setError('');

    const operationId = `join_group_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    // Prepare merged state (preserve existing userId/createdAt etc.)
    const nowISO = new Date().toISOString();

    const mergedGameData = (prev = {}) => {
      const fanTypeBackend = foundGroup.fanType;
      return {
        ...prev,
        groupMode: 'join',
        groupId: foundGroup.id,
        groupName: foundGroup.name || '',
        groupDescription: foundGroup.description || '',
        inviteCode: foundGroup.inviteCode || foundGroup.id,
        isGroupCreator: false,

        team: foundGroup.team,
        teamId: foundGroup.teamId || '',
        league: foundGroup.league || 'NFL',
        conference: foundGroup.conference || '',
        division: foundGroup.division || '',
        season: foundGroup.season || getFootballSeasonYear(),

        fanType: fanTypeBackend, // normalized

        teamSelected: true,
        teamPreselected: true,
        fanTypePreselected: true,

        // Bypass paywall on join; members will consume their own credits later
        _groupJoinAccess: true,
        _skipPayment: true,
        _alreadyPaid: true,

        // Flow flags
        _blockAutoSave: true,
        _joinInProgress: true,
        _operationId: operationId,
        _paymentFlowActive: false,
        _redirectingToStripe: false,

        // Reset analysis content for a clean start in this group
        participantName: prev.participantName || '',
        avatar: prev.avatar || null,
        formActivated: true,
        positionGrades: {},
        coachGrades: {},
        predictions: {},

        status: 'draft',
        completionPercentage: 0,

        userId: prev.userId || user.uid,
        createdAt: prev.createdAt || nowISO,
        lastUpdated: nowISO,
      };
    };

    try {
      try { sessionStorage.removeItem('ga-sticky-id'); } catch {}

      setGameData((prev) => mergedGameData(prev));

      // nav context
      const navigationContext = {
        fromGroupJoin: true,
        directInvite: !!authUserDirectJoin,
        timestamp: Date.now(),
        groupData: {
          id: foundGroup.id,
          name: foundGroup.name,
          team: foundGroup.team,
          fanType: foundGroup.fanType,
          league: foundGroup.league,
          conference: foundGroup.conference,
          division: foundGroup.division,
        },
        preselectedData: {
          team: foundGroup.team,
          fanType: foundGroup.fanType,
          teamSelected: true,
          fanTypeSelected: true,
        },
      };
      try {
        localStorage.setItem('sbe-navigation-context', JSON.stringify(navigationContext));
      } catch {}

      // Firestore membership write (best-effort)
      if (hasFirebase && FieldValue && user?.uid) {
        try {
          const groupRef = window.db.collection('groups').doc(foundGroup.id);
          const groupDoc = await groupRef.get();
          if (!groupDoc.exists) throw new Error('Group document no longer exists');

          const updateData = {
            [`members.${user.uid}`]: {
              userId: user.uid,
              email: user.email || null,
              joinedAt: FieldValue.serverTimestamp(),
              status: 'joined',
            },
            memberCount: FieldValue.increment(1),
            lastUpdated: FieldValue.serverTimestamp(),
          };

          logger.component('🔥 Group join update', {
            userId: user.uid,
            groupId: foundGroup.id,
            updateFields: Object.keys(updateData),
          });

          await groupRef.update(updateData);
          logger.success('✅ Added user to group members map');
        } catch (firestoreError) {
          logger.error('❌ Firestore update failed:', firestoreError);

          if (
            firestoreError.code === 'permission-denied' ||
            String(firestoreError.message || '').includes('insufficient permissions')
          ) {
            logger.component('⚠️ Continuing in local-only mode due to permissions');
            setError(
              'Joined in local mode—some features may be limited. Contact support if issues persist.'
            );
          } else {
            throw firestoreError;
          }
        }
      }

      // Clean URL after successful lookup/intent
      stripInviteFromUrl(foundGroup.inviteCode || normalizedCode);

      // Clear any upstream navigation data
      if (setNavigationData) setNavigationData(null);

      // Move to profile setup
      logger.completion('🚀 Navigating to profile setup with preselected team/fanType');
      setCurrentStep('profile');
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);

      // Post-join cleanup of temporary flags
      cleanupTimerRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        setGameData((prev) => {
          if (!prev || prev._operationId !== operationId) return prev;
          const next = { ...prev };
          delete next._blockAutoSave;
          delete next._joinInProgress;
          delete next._operationId;
          logger.debug('🧹 Cleaned auto-save flags post-join');
          return next;
        });
      }, 1200);
    } catch (err) {
      logger.error('❌ Error joining group:', err);

      if (err.code === 'permission-denied' || err.message?.includes('insufficient permissions')) {
        setError(
          'Permission denied. You may not have access to join this group, or there may be a temporary server issue.'
        );
      } else if (err.message?.includes('not have access')) {
        setError('This group may be private or no longer accepting members.');
      } else {
        setError('Failed to join group. Please try again or contact support if the issue persists.');
      }

      setGameData((prev) => {
        const next = { ...(prev || {}) };
        delete next._blockAutoSave;
        delete next._joinInProgress;
        delete next._operationId;
        return next;
      });
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [foundGroup, setGameData, setCurrentStep, setNavigationData, hasFirebase, FieldValue, stripInviteFromUrl, normalizedCode, authUserDirectJoin]);

  // Auto-fill / smart invite
  useEffect(() => {
    if (autoFillProcessed || autoSearchInProgress) return;
    if (propInviteCode && autoFilled) {
      const cleaned = String(propInviteCode).trim().toUpperCase();
      if (sessionStorage.getItem(`invite-accepted:${cleaned}`) === '1') return;

      logger.completion('🎯 Processing smart invite', {
        propInviteCode,
        cleaned,
        autoFilled,
        authUserDirectJoin,
      });

      setInviteCode(cleaned);
      setAutoFillProcessed(true);
      setAutoSearchInProgress(true);
      setError('');

      const searchWithCode = async () => {
        logger.component('🚀 Auto-searching group from smart invite:', cleaned);

        if (!cleaned) {
          setError('Invalid invite code from navigation');
          setAutoSearchInProgress(false);
          return;
        }

        setLoading(true);
        setError('');
        setFoundGroup(null);

        try {
          const group = await loadGroupByCode(cleaned);
          if (!group) {
            setError('Group not found. Please check your invite code.');
            return;
          }
          if (!group.team || !group.fanType) {
            setError('This group is not fully configured yet. Please wait for the creator to finish setup.');
            return;
          }
          if (group.status === 'completed' || group.status === 'archived') {
            setError('This group is no longer accepting new members.');
            return;
          }
          logger.success('✅ Found valid group from auto-search', group);
          if (mountedRef.current) setFoundGroup(group);
        } catch (err) {
          logger.error('❌ Error auto-searching for group:', err);
          setError('Failed to search for group. Please try again.');
        } finally {
          if (mountedRef.current) {
            setLoading(false);
            setAutoSearchInProgress(false);
          }
        }
      };

      const timerId = setTimeout(searchWithCode, authUserDirectJoin ? 200 : 500);
      return () => clearTimeout(timerId);
    }
  }, [propInviteCode, autoFilled, authUserDirectJoin, autoFillProcessed, autoSearchInProgress, hasFirebase]);

  // Previously blocked joining after starting an analysis; now allow but show a soft warning via banner elsewhere
  useEffect(() => {
    const isSmartInvite = (propInviteCode && autoFilled) || autoFillProcessed;
    const isAuthDirectJoin = authUserDirectJoin && propInviteCode;

    if (!isSmartInvite && !isAuthDirectJoin) {
      const hasAnalysis =
        !!gameData?.team ||
        (gameData?.positionGrades && Object.keys(gameData.positionGrades).length > 0) ||
        (gameData?.coachGrades && Object.keys(gameData.coachGrades).length > 0) ||
        (gameData?.predictions && Object.keys(gameData.predictions).length > 0) ||
        gameData?.status === 'submitted';

      if (hasAnalysis) {
        // do not block; a draft will be reset on join
        if (error) setError('');
      }
    } else if (error) {
      setError('');
    }
  }, [gameData, propInviteCode, autoFilled, autoFillProcessed, authUserDirectJoin]);

  // Handlers
  const handleBackClick = useCallback(() => {
    logger.component('🔄 Back to dashboard from join flow');
    if (setNavigationData) setNavigationData(null);

    if (setGameData) {
      setGameData((prev) => {
        const next = { ...prev };
        // Clean up any lingering join/group flags
        delete next.groupMode;
        delete next.groupId;
        delete next.groupName;
        delete next.groupDescription;
        delete next.inviteCode;
        delete next.isGroupCreator;
        delete next.teamPreselected;
        delete next.fanTypePreselected;
        delete next._blockAutoSave;
        delete next._joinInProgress;
        delete next._operationId;

        // Reset the core analysis state for a fresh start
        return {
          ...next,
          team: null,
          teamId: null,
          league: null,
          conference: null,
          division: null,
          season: getFootballSeasonYear(),
          teamSelected: false,
          fanType: null,
          formActivated: false,
          participantName: '',
          avatar: null,
          positionGrades: {},
          coachGrades: {},
          predictions: {},
          completionPercentage: 0,
          status: null,
          _paymentFlowActive: false,
          _redirectingToStripe: false,
          lastReset: new Date().toISOString(),
        };
      });
    }

    setInviteCode('');
    setError('');
    setLoading(false);
    setFoundGroup(null);
    setAutoFillProcessed(false);
    setAutoSearchInProgress(false);

    if (setCurrentStep) setCurrentStep('dashboard');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  }, [setGameData, setCurrentStep, setNavigationData]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !loading && !foundGroup && normalizedCode) {
        e.preventDefault();
        searchGroup();
      }
    },
    [loading, foundGroup, normalizedCode, searchGroup]
  );

  const onInviteChange = useCallback((e) => {
    const val = e.target.value;
    // IME-safe; while composing, don't transform
    // @ts-ignore
    if (e.nativeEvent && e.nativeEvent.isComposing) {
      setInviteCode(val);
    } else {
      setInviteCode(val.toUpperCase());
    }
  }, []);

  // UI
  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '2rem 1rem', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🤝</div>
        <h1 style={{ fontSize: '2rem', fontWeight: 600, color: '#1f2937', marginBottom: '0.5rem' }}>
          Join a Group
        </h1>
        <p style={{ color: '#6b7280', fontSize: '1rem' }}>
          Enter your group's invite code to join their analysis
        </p>
      </div>

      {propInviteCode && autoFilled && (
        <div
          style={{
            backgroundColor: '#d1fae5',
            border: '1px solid #a7f3d0',
            borderRadius: 8,
            padding: '1rem',
            marginBottom: '1.5rem',
            textAlign: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <span>🔗⚡</span>
            <strong style={{ color: '#059669' }}>Direct Smart Invite!</strong>
          </div>
          <p style={{ color: '#047857', fontSize: 14, margin: '8px 0 0' }}>
            Processing invite "{propInviteCode}" for authenticated user.
          </p>
          {foundGroup && (
            <p style={{ color: '#047857', fontSize: 14, margin: '8px 0 0' }}>
              ✅ Group found! Ready to join with preselected team & analysis level.
            </p>
          )}
        </div>
      )}

      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', fontWeight: 500, marginBottom: 8, color: '#374151' }}>
          Group Invite Code
        </label>
        <input
          type="text"
          value={inviteCode}
          onChange={onInviteChange}
          onKeyDown={handleKeyDown}
          placeholder="Enter invite code"
          disabled={loading}
          style={{
            width: '100%',
            padding: '0.75rem',
            border: '2px solid #d1d5db',
            borderRadius: 6,
            fontSize: '1rem',
            backgroundColor: autoFillProcessed ? '#f0f9ff' : 'white',
          }}
          aria-label="Group invite code"
        />
      </div>

      {error && (
        <div
          style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 6,
            padding: '0.75rem',
            color: '#dc2626',
            fontSize: 14,
            marginBottom: '1rem',
          }}
          aria-live="polite"
        >
          {error}
        </div>
      )}

      {foundGroup && (
        <div
          style={{
            border: '2px solid #3b82f6',
            borderRadius: 8,
            padding: '1rem',
            marginBottom: '1.5rem',
            backgroundColor: '#eff6ff',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span>🔗⚡</span>
            <strong style={{ color: '#1d4ed8' }}>{foundGroup.name}</strong>
            {authUserDirectJoin && (
              <span
                style={{
                  fontSize: 12,
                  color: '#dc2626',
                  backgroundColor: '#fef2f2',
                  padding: '2px 6px',
                  borderRadius: 4,
                }}
              >
                ⚡ Direct smart invite (authenticated user)
              </span>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12 }}>
            <div>
              <div style={{ fontWeight: 500, color: '#374151', fontSize: 14 }}>TEAM (Pre-selected)</div>
              <div style={{ color: '#1f2937' }}>{foundGroup.team}</div>
              <div style={{ color: '#6b7280', fontSize: 14 }}>
                {foundGroup.league} {foundGroup.conference ? `- ${foundGroup.conference}` : ''}
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 500, color: '#374151', fontSize: 14 }}>ANALYSIS LEVEL (Pre-selected)</div>
              <div style={{ color: '#1f2937' }}>{mapFanTypeLabel(foundGroup.fanType)}</div>
              <div style={{ color: '#6b7280', fontSize: 14 }}>
                {foundGroup.fanType === 'casual' ? 'Free' : 'Premium (member credit required)'}
              </div>
            </div>
          </div>

          <div
            style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #22c55e',
              borderRadius: 6,
              padding: '0.75rem',
              marginBottom: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span>✅</span>
              <strong style={{ color: '#15803d' }}>Ready to Join!</strong>
            </div>
            <div style={{ fontSize: 14, color: '#15803d' }}>
              • Team & analysis level are set by the creator<br />
              • You’ll enter your name & pick an avatar next<br />
              • You’ll go straight to profile setup after joining
            </div>
          </div>

          {groupPreview && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #d1d5db' }}>
              <div style={{ fontSize: 14, color: '#374151', fontWeight: 600, marginBottom: 6 }}>Group Preview</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                {groupPreview.members.length} member{groupPreview.members.length !== 1 ? 's' : ''}
                {typeof groupPreview.submitted === 'number' ? ` • ${groupPreview.submitted} submitted` : ''}
              </div>
              {groupPreview.members.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {groupPreview.members.map((m, idx) => (
                    <div key={m.id || idx} style={{ fontSize: 12, background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', color: '#374151' }}>
                      {m.email || m.name || m.id?.slice(0,6) || 'Member'}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              logger.component('🎯 Join clicked');
              joinGroup();
            }}
            disabled={loading}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: loading ? '#9ca3af' : '#22c55e',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              fontSize: '1rem',
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            {loading ? (
              <>
                <div
                  style={{
                    width: 16,
                    height: 16,
                    border: '2px solid rgba(255,255,255,0.25)',
                    borderTop: '2px solid #ffffff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                Joining Group...
              </>
            ) : (
              '⚡ Join "' + foundGroup.name + '" (Team & Level Pre-selected)'
            )}
          </button>
        </div>
      )}

      {!foundGroup && !loading && normalizedCode && (
        <button
          type="button"
          onClick={searchGroup}
          style={{
            width: '100%',
            padding: '0.75rem',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            fontSize: '1rem',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Continue
        </button>
      )}

      <div style={{ display: 'flex', gap: 16, marginTop: '1.5rem' }}>
        <button
          type="button"
          onClick={handleBackClick}
          style={{
            flex: 1,
            padding: '0.75rem',
            backgroundColor: 'transparent',
            color: '#6b7280',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: '1rem',
            cursor: 'pointer',
          }}
        >
          ← Back
        </button>
        <button
          type="button"
          style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'transparent',
            color: '#6b7280',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: '1rem',
            cursor: 'pointer',
          }}
          onClick={() => alert('Enter the invite code provided by the group creator.\nWe’ll preload the team and analysis level for you.')}
        >
          ❓ Help
        </button>
      </div>

      <style>{`@keyframes spin { 0% { transform: rotate(0deg) } 100% { transform: rotate(360deg) } }`}</style>
    </div>
  );
};

export default JoinGroupFlow;